import * as React from 'react';
// Import enumerations.
import {
	AppThemeColor,
	ButtonSize,
} from '@app/enumerations';
// Import props.
import {
	InputButtonProps,
	IconProps,
} from '@app/props';
import {
	SvgIconProps
} from '@mui/material';

interface UseInputButtonParameters {
	id: string;
	size: ButtonSize;
	buttonType: AppThemeColor;
	endAdornmentIcon?: React.ComponentType<IconProps | SvgIconProps>;
	startAdornmentIcon?: React.ComponentType<IconProps | SvgIconProps>;
	disabled?: boolean;
	label: string;
	onClick?: () => void;
}

interface UseInputButtonResult {
	focusToButton: () => void;
	applyProps: () => InputButtonProps;
}

const useInputButton = (params: UseInputButtonParameters): UseInputButtonResult => {

	// #region exported methods

	/**
	 * Focuses to element. 
	 **/
	const focusToButton = (): void => {
		setQueuedButtonToFocus(true);
	};

	/**
	 * Returns props for form input. 
	 **/
	const applyProps = (): InputButtonProps => {
		return {
			id: params.id,
			size: params.size,
			buttonType: params.buttonType,
			endAdornmentIcon: params.endAdornmentIcon,
			startAdornmentIcon: params.startAdornmentIcon,
			disabled: params.disabled,
			label: params.label,
			onClick: params.onClick,
			buttonRef: registerRef,
		}
	};

	// #endregion exported methods


	// #region private methods.

	const registerRef = (ref: React.RefObject<HTMLButtonElement>): void => {
		if (buttonRef === undefined
			|| buttonRef.current === null) {
			setButtonRef(ref);
		}
	}

	// #endregion private methods.


	// #region variables

	const [buttonRef, setButtonRef] = React.useState<React.RefObject<HTMLButtonElement> | undefined>(undefined);
	const [queuedButtonToFocus, setQueuedButtonToFocus] = React.useState<boolean>(false);

	// #endregion variables


	// #region life-cycle methods.

	// Called when button ref or when the focus-to flag is updated.
	React.useEffect(() => {
		// If there is a queued button focus-to flag and the reference is ready, then proceed to focus on the button.
		if (queuedButtonToFocus
			&& buttonRef !== undefined
			&& buttonRef.current !== null) {
			// If button is already focused, then do not attempt to do it again.
			if (document.activeElement !== buttonRef.current) {
				buttonRef.current.focus();
			}
			// Reset the queued input to focus.
			setQueuedButtonToFocus(false);
		}
	}, [queuedButtonToFocus, buttonRef]);

	// #endregion life-cycle methods.


	return {
		focusToButton: focusToButton,
		applyProps: applyProps,
	};
}

// Export parameters.
export {
	UseInputButtonParameters,
}

// Export results.
export {
	UseInputButtonResult,
}

export default useInputButton;